<script lang="ts">
  import { MediaQuery } from "svelte/reactivity";
  import { Button } from "@poolsideai/web-components";

  interface Props {
    currentPage: number;
    totalPages: number;
    isLoading?: boolean;
    optionsShown?: number;
    onPageChange: (page: number) => void;
  }

  const {
    currentPage,
    totalPages,
    isLoading = false,
    optionsShown = 7,
    onPageChange,
  }: Props = $props();

  const narrow = new MediaQuery("(max-width: 479px)");
  const medium = new MediaQuery("(max-width: 639px)");

  const effectiveOptionsShown = $derived(
    narrow.current
      ? Math.min(optionsShown, 3)
      : medium.current
        ? Math.min(optionsShown, 5)
        : optionsShown,
  );

  function goToPage(page: number) {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  }

  function nextPage() {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  }

  function prevPage() {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  }

  // calculate which page numbers to show (effectiveOptionsShown items to avoid layout shifts)
  const visiblePages = $derived(() => {
    if (totalPages <= effectiveOptionsShown) {
      // show all pages if fewer than or equal to effectiveOptionsShown
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];

    // need at least 5 slots for double ellipsis mode (1 ... x ... n)
    if (effectiveOptionsShown < 5) {
      // single ellipsis mode: split available page slots between start and end
      const pageSlots = effectiveOptionsShown - 1; // reserve 1 slot for ellipsis
      const startSlots = Math.ceil(pageSlots / 2);
      const endSlots = pageSlots - startSlots;

      for (let i = 1; i <= startSlots; i++) {
        pages.push(i);
      }
      pages.push("...");
      for (let i = totalPages - endSlots + 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    // effectiveOptionsShown >= 5: full mode with potential double ellipsis
    const pagesAtEdge = effectiveOptionsShown - 2; // pages shown at start/end (excluding boundary and ellipsis)
    const startThreshold = pagesAtEdge - 1;
    const endThreshold = totalPages - pagesAtEdge + 2;
    const middlePages = effectiveOptionsShown - 4; // pages shown in middle mode (excluding first, last, and 2 ellipses)
    const middleOffset = Math.floor(middlePages / 2);

    if (currentPage <= startThreshold) {
      // show first pagesAtEdge pages + ellipsis + last page
      for (let i = 1; i <= pagesAtEdge; i++) {
        pages.push(i);
      }
      pages.push("...");
      pages.push(totalPages);
    } else if (currentPage >= endThreshold) {
      // show first page + ellipsis + last pagesAtEdge pages
      pages.push(1);
      pages.push("...");
      for (let i = totalPages - pagesAtEdge + 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // show first page + ellipsis + middle pages + ellipsis + last page
      pages.push(1);
      pages.push("...");
      for (let i = currentPage - middleOffset; i <= currentPage + middleOffset; i++) {
        pages.push(i);
      }
      pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  });
</script>

{#if totalPages > 1}
  <div class="flex items-center justify-center gap-2">
    <Button
      class="text-xs"
      disabled={currentPage === 1 || isLoading}
      onclick={prevPage}
      size="sm"
      type="button"
      variant="secondary"
    >
      Previous
    </Button>

    <div class="flex items-center gap-1">
      {#each visiblePages() as pageItem}
        {#if typeof pageItem === "number"}
          <Button
            class="text-xs"
            disabled={isLoading}
            onclick={() => goToPage(pageItem)}
            size="sm"
            type="button"
            variant={pageItem === currentPage ? "primary" : "tertiary"}
          >
            {pageItem}
          </Button>
        {:else}
          <span class="px-2 text-sm text-foreground-secondary">...</span>
        {/if}
      {/each}
    </div>

    <Button
      class="text-xs"
      disabled={currentPage === totalPages || isLoading}
      onclick={nextPage}
      size="sm"
      type="button"
      variant="secondary"
    >
      Next
    </Button>
  </div>
{/if}
